import { StatusCodes } from "http-status-codes"
import { useCallback, useContext, useEffect, useState } from "react"
import { Navigate, useNavigate, useParams } from "react-router-dom"
import { useHashTab } from "../../../../../../hooks/useHashTab"
import { digitalManagerApi } from "../../../../../../services/api"
import type { DiMaDetailsHeaderProps } from "../../../../../components/DiMaDetailsHeader"
import { DiMaProgress } from "../../../../../components/DiMaProgress"
import { DetailsHeaderTab } from "../../../../../components/Helpers"
import { UserContext } from "../../../../../context/UserContext"
import { DeviceRolePermissionGet } from "../../../../../models/DeviceRolePermission"
import { DeviceRolePermissionApplicationRoles } from "./applicationRoles_tab/DeviceRolePermissionApplicationRoles"
import { DeviceRolePermissionDetails } from "./details_tab/DeviceRolePermissionDetails"

export function DeviceRolePermissionContainer() {
  const { deviceId, roleId, permissionId } = useParams()
  const { user, isLoadingUser } = useContext(UserContext)
  const navigate = useNavigate()
  const { activeTab, handleTabChange } = useHashTab("details")

  const [deviceRolePermission, setDeviceRolePermission] =
    useState<DeviceRolePermissionGet>()
  const [reloadEntity, setReloadEntity] = useState<boolean>(true)

  const returnURL = `/devices/${deviceId}/roles/${roleId}#permissions`

  const reloadDeviceRolePermission = useCallback(() => setReloadEntity(true), [])

  useEffect(() => {
    if (deviceId && roleId && permissionId && reloadEntity === true) {
      digitalManagerApi
        .get<DeviceRolePermissionGet>(
          `/devices/${deviceId}/roles/${roleId}/permissions/${permissionId}`
        )
        .then((res) => {
          if (res.status === StatusCodes.OK) {
            setDeviceRolePermission(res.data)
            setReloadEntity(false)
          }
        })
        .catch((err) => {
          if (err.response.status) {
            navigate(returnURL)
          }
        })
    }
  }, [deviceId, navigate, permissionId, reloadEntity, returnURL, roleId])

  if (!deviceId || !roleId || !permissionId) {
    return <Navigate to="/devices" />
  }

  if (!deviceRolePermission || isLoadingUser || !user) {
    return <DiMaProgress />
  }

  const detailsHeaderProps: DiMaDetailsHeaderProps = {
    url: returnURL,
    title: deviceRolePermission.permission.name,
    label: "Permission ID",
    subtitle: deviceRolePermission.permission.id,
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
      <DetailsHeaderTab
        detailsHeaderProps={detailsHeaderProps}
        tabItems={[
          { id: "details", label: "Details" },
          { id: "app-roles", label: "App Roles" },
        ]}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />

      {activeTab === "details" && (
        <DeviceRolePermissionDetails
          deviceRolePermission={deviceRolePermission}
          user={user}
        />
      )}

      {activeTab === "app-roles" && (
        <DeviceRolePermissionApplicationRoles
          deviceRolePermission={deviceRolePermission}
          reloadDeviceRolePermission={reloadDeviceRolePermission}
          user={user}
        />
      )}
    </div>
  )
}
